import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BinghattiHillsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    agreement: false
  });

  const [isVisible, setIsVisible] = useState({});
  const [currentImage, setCurrentImage] = useState(0);
  const heroRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Hero image slideshow
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const heroImages = [
    '/binghatti/binghatti hills/hills-exterior-a.avif',
    '/binghatti/binghatti hills/hills-exterior-c.avif',
    '/binghatti/binghatti hills/hills-exterior-d.avif',
    '/binghatti/binghatti hills/hills-outdoor-a.avif'
  ];

  const amenities = [
    {
      title: "Luxury Living Spaces",
      description: "Experience the epitome of luxury with our meticulously designed living spaces featuring premium finishes and contemporary aesthetics.",
      image: "/binghatti/binghatti hills/hills-livingroom.avif"
    },
    {
      title: "Gourmet Kitchens",
      description: "State-of-the-art kitchens equipped with the finest appliances and elegant cabinetry for the ultimate culinary experience.",
      image: "/binghatti/binghatti hills/hills-kitchen.avif"
    },
    {
      title: "Serene Bedrooms",
      description: "Peaceful retreats designed for comfort and relaxation, featuring premium bedding and stunning views.",
      image: "/binghatti/binghatti hills/hills-bedroom.avif"
    },
    {
      title: "Outdoor Living",
      description: "Expansive outdoor spaces perfect for entertaining, relaxation, and enjoying the beautiful surroundings.",
      image: "/binghatti/binghatti hills/hills-outdoor-a.avif"
    }
  ];

  const galleryImages = [
    "/binghatti/binghatti hills/hills-living-a.avif",
    "/binghatti/binghatti hills/hills-bedroom-a.avif",
    "/binghatti/binghatti hills/hills-exterior-c.avif",
    "/binghatti/binghatti hills/hills-exterior-d.avif",
    "/binghatti/binghatti hills/hills-outdoor-a.avif",
    "/binghatti/binghatti hills/binghatti-hillside-bedroom-1.webp"
  ];

  const faqs = [
    {
      question: "Where is Binghatti Hills located?",
      answer: "Binghatti Hills is strategically located in Dubai Hills Estate, offering easy access to major highways and key destinations."
    },
    {
      question: "What types of properties are available at Binghatti Hills?",
      answer: "The development offers luxury villas and premium residences with world-class amenities and finishes."
    },
    {
      question: "What makes Binghatti Hills unique?",
      answer: "Binghatti Hills stands out with its exclusive location, premium amenities, and innovative design that creates the perfect blend of luxury and comfort."
    }
  ];

  return (
    <div className="flex flex-col min-h-screen font-sans bg-white">
      <Navbar />
      <main className="flex-1">
        {/* Animated Hero Section */}
        <section ref={heroRef} className="relative w-full h-screen flex flex-col items-center justify-center overflow-hidden">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${
                index === currentImage ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image}
                alt={`Binghatti Hills ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-black bg-opacity-40 z-10" />
          <div className="relative z-20 w-full flex flex-col items-center justify-center text-center px-4">
            <h1 
              className="text-4xl md:text-6xl font-light mb-4 text-white font-sans tracking-tight animate-fade-in"
              style={{fontFamily: 'Roboto, sans-serif'}}
            >
              BINGHATTI HILLS
            </h1>
            <p className="text-xl md:text-2xl text-white/90 font-light animate-fade-in-delay">
              Where Luxury Meets Nature
            </p>
          </div>
        </section>

        {/* Animated About Section */}
        <section 
          id="about"
          data-animate
          className={`py-20 bg-gradient-to-br from-gray-50 to-white transition-all duration-1000 ${
            isVisible.about ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>
                ABOUT BINGHATTI HILLS
              </h2>
              <p className="text-lg text-gray-600 max-w-4xl mx-auto font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>
                A prestigious development that redefines luxury living with its innovative design, world-class amenities, and breathtaking views of Dubai's most iconic landmarks.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center transform hover:scale-105 transition-transform duration-300">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Premium Location</h3>
                <p className="text-gray-600">Dubai Hills Estate</p>
              </div>
              <div className="text-center transform hover:scale-105 transition-transform duration-300">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Luxury Villas</h3>
                <p className="text-gray-600">Premium residences</p>
              </div>
              <div className="text-center transform hover:scale-105 transition-transform duration-300">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">World-Class Amenities</h3>
                <p className="text-gray-600">Exclusive facilities</p>
              </div>
            </div>
          </div>
        </section>

        {/* Animated Amenities Section */}
        <section 
          id="amenities"
          data-animate
          className={`py-20 bg-white transition-all duration-1000 ${
            isVisible.amenities ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-tight font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>
                LUXURY AMENITIES
              </h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {amenities.map((amenity, index) => (
                <div 
                  key={index}
                  className={`transform transition-all duration-700 ${
                    isVisible[`amenity-${index}`] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                  }`}
                  data-animate
                  id={`amenity-${index}`}
                >
                  <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                    <img 
                      src={amenity.image} 
                      alt={amenity.title}
                      className="w-full h-96 object-cover transform hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <h3 className="text-2xl font-light mb-3 font-sans tracking-tight" style={{fontFamily: 'Roboto, sans-serif'}}>
                        {amenity.title}
                      </h3>
                      <p className="text-white/90 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>
                        {amenity.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Animated Gallery Section */}
        <section 
          id="gallery"
          data-animate
          className={`py-20 bg-gray-50 transition-all duration-1000 ${
            isVisible.gallery ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-tight font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>
                GALLERY
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((image, index) => (
                <div 
                  key={index}
                  className={`transform transition-all duration-500 hover:scale-105 ${
                    isVisible[`gallery-${index}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  data-animate
                  id={`gallery-${index}`}
                >
                  <div className="relative overflow-hidden rounded-lg shadow-lg">
                    <img 
                      src={image} 
                      alt={`Binghatti Hills Gallery ${index + 1}`}
                      className="w-full h-64 object-cover transform hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Animated FAQ Section */}
        <section 
          id="faq"
          data-animate
          className={`py-20 bg-white transition-all duration-1000 ${
            isVisible.faq ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>
                FREQUENTLY ASKED QUESTIONS
              </h2>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className={`bg-white rounded-lg shadow-lg p-6 transform transition-all duration-500 hover:shadow-xl ${
                    isVisible[`faq-${index}`] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                  }`}
                  data-animate
                  id={`faq-${index}`}
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Animated Contact Form */}
        <section 
          id="contact"
          data-animate
          className={`py-20 bg-gradient-to-br from-gray-900 to-black transition-all duration-1000 ${
            isVisible.contact ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-light text-white mb-6 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>
                EXPRESS YOUR INTEREST
              </h2>
              <p className="text-lg text-gray-300 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>
                Ready to discover your perfect luxury home at Binghatti Hills? Contact us today.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-600 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent bg-gray-800 text-white font-sans"
                    style={{fontFamily: 'Roboto, sans-serif'}}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-600 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent bg-gray-800 text-white font-sans"
                    style={{fontFamily: 'Roboto, sans-serif'}}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-600 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent bg-gray-800 text-white font-sans"
                  style={{fontFamily: 'Roboto, sans-serif'}}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-600 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent bg-gray-800 text-white font-sans"
                  style={{fontFamily: 'Roboto, sans-serif'}}
                ></textarea>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="agreement"
                  checked={formData.agreement}
                  onChange={handleChange}
                  className="h-4 w-4 text-white focus:ring-white border-gray-600 rounded bg-gray-800"
                />
                <label className="ml-2 text-sm text-gray-300 font-sans" style={{fontFamily: 'Roboto, sans-serif'}}>
                  I agree to receive exclusive updates & other communications
                </label>
              </div>
              <div className="text-center">
                <button
                  type="submit"
                  className="px-8 py-4 bg-white text-black font-medium hover:bg-gray-200 transition-colors duration-200 font-sans transform hover:scale-105"
                  style={{fontFamily: 'Roboto, sans-serif'}}
                >
                  SUBMIT
                </button>
              </div>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BinghattiHillsPage; 